import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/api/api.js";
import { Form, Button, Row, Col, Card, CardBody } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { capitalizeFirstLetter } from "@/utils/stringUtils.js";
import CertificateUpload from "@/pages/user/Contacts/CertificateUpload.jsx";

const CompanyContactsEdit = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    taxIdentification: "",
    businessName: "",
    street: "",
    exterior: "",
    interior: "",
    suburb: "",
    town: "",
    state: "",
    postalCode: "",
    phone: "",
    emailContact: "",
    type: "",
  });
  const [overrideFields, setOverrideFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const overrides = {};
    const overrideKeys = [];
    params.forEach((value, key) => {
      overrides[key] = value;
      overrideKeys.push(key);
    });
    setOverrideFields(overrideKeys);

    api
      .get(`/contacts/${contactId}`)
      .then(({ data }) => {
        // Construimos datos base desde el endpoint
        const baseData = {
          taxIdentification: data.taxIdentification || "",
          businessName: data.businessName || "",
          street: data.street || "",
          exterior: data.exterior || "",
          interior: data.interior || "",
          suburb: data.suburb || "",
          town: data.town || "",
          state: data.state || "",
          postalCode: data.postalCode || "",
          phone: data.phone || "",
          emailContact: data.emailContact || "",
          type: data.type || "",
        };
        // Aplicamos overrides por URL *después* de los datos del endpoint
        setFormData({
          ...baseData,
          ...overrides,
        });
      })
      .catch((err) => {
        console.error("Error al cargar contacto:", err);
        setError(t("Error loading contact"));
      })
      .finally(() => setLoading(false));
  }, [contactId, search, t]);
  const handleUploadSuccess = (data) => {
    const { id, edit, fields } = data.contact;

    if (!edit) {
      navigate("/contacts");
      return;
    }

    const params = new URLSearchParams();
    Object.entries(fields).forEach(([key, value]) => {
      params.append(key, value);
    });

    navigate(`/contacts/${id}/edit?${params.toString()}`);
  };

  const handleChange = ({ target: { name, value } }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/contacts/${contactId}`, formData, {
        headers: {
          "Content-Type": "application/merge-patch+json",
        },
      });
      navigate("/contacts");
    } catch (err) {
      console.error("Error updating contact:", err);
      setError(t("Error updating contact"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>{t("Loading...")}</p>;

  return (
    <>
      <Col xs={12}>
        <Card>
          <CardBody>
            <CertificateUpload onUploadSuccess={handleUploadSuccess} />
          </CardBody>
        </Card>
      </Col>
      <Col xs={12}>
        <Card className="mt-4">
          <Card.Body>
            <h3>{t("Edit Contact")}</h3>
            {error && <p className="text-danger">{error}</p>}
            <Form onSubmit={handleSubmit}>
              <Row className="g-2">
                {Object.entries(formData).map(([key, value]) => (
                  <Col md={6} key={key}>
                    <Form.Group controlId={key}>
                      <Form.Label>
                        {capitalizeFirstLetter(t(key))}
                        {overrideFields.includes(key) && (
                          <span className="ms-1 text-warning">*</span>
                        )}
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name={key}
                        value={value}
                        onChange={handleChange}
                        required={[
                          "phone",
                          "taxIdentification",
                          "businessName",
                          "street",
                          "exterior",
                          "suburb",
                          "town",
                          "state",
                          "postalCode",
                        ].includes(key)}
                        style={
                          overrideFields.includes(key)
                            ? { backgroundColor: "#fff3cd" }
                            : {}
                        }
                      />
                    </Form.Group>
                  </Col>
                ))}
              </Row>
              <div className="d-flex align-items-center gap-2 mt-3">
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving
                    ? t("Saving...")
                    : capitalizeFirstLetter(t("update contact"))}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate("/contacts")}
                >
                  {t("Cancel")}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Col>
    </>
  );
};

export default CompanyContactsEdit;
